import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Slider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  useTheme,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material';
import {
  Info as InfoIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

// Componente estilizado para o slider
const StyledSlider = styled(Slider)(({ theme }) => ({
  height: 8,
  '& .MuiSlider-track': {
    border: 'none',
    backgroundImage: 'linear-gradient(90deg, #3f51b5, #f50057)',
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '2px solid #3f51b5',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: '0 0 0 8px rgba(63, 81, 181, 0.16)',
    },
    '&:before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: '#3f51b5',
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&:before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
    '& > *': {
      transform: 'rotate(45deg)',
    },
  },
}));

// Componente para o simulador de planos
const PlanSimulator: React.FC = () => {
  const theme = useTheme();
  const userData = useSelector((state: RootState) => state.user.data);
  
  // Estados para os inputs do simulador
  const [leadCount, setLeadCount] = useState(50); // Valor padrão se não houver dados
  const [productCount, setProductCount] = useState(20); // Valor padrão se não houver dados
  const [whatsappUsage, setWhatsappUsage] = useState(50); // Percentual de uso do WhatsApp
  const [billingCycle, setBillingCycle] = useState<string>('monthly');
  
  // Calcular o plano recomendado com base nos inputs
  const recommendedPlan = useMemo(() => {
    if (leadCount > 200 || productCount > 50 || whatsappUsage > 75) {
      return {
        name: 'Premium',
        color: theme.palette.secondary.main,
        gradient: 'linear-gradient(135deg, #f50057 0%, #c51162 100%)',
        monthlyPrice: 99.90
      };
    } else if (leadCount > 50 || productCount > 20 || whatsappUsage > 30) {
      return {
        name: 'Intermediário',
        color: theme.palette.primary.main,
        gradient: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)',
        monthlyPrice: 59.90
      };
    }
    return {
      name: 'Free',
      color: '#6c757d',
      gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
      monthlyPrice: 0
    };
  }, [leadCount, productCount, whatsappUsage, theme]);
  
  // Calcular economia anual
  const pricing = useMemo(() => {
    const monthlyPrice = recommendedPlan.monthlyPrice;
    
    // 20% de desconto para planos anuais
    const annualPrice = monthlyPrice * 12 * 0.8;
    const savings = monthlyPrice * 12 - annualPrice;
    
    return {
      monthly: monthlyPrice,
      annual: annualPrice / 12,
      totalAnnual: annualPrice,
      savings: savings
    };
  }, [recommendedPlan]);

  // Manipuladores de eventos
  const handleBillingCycleChange = (event: SelectChangeEvent) => {
    setBillingCycle(event.target.value);
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: '16px', 
        mb: 4,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: '5px', 
        background: 'linear-gradient(90deg, #3f51b5, #f50057)'
      }} />
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CalculateIcon sx={{ color: theme.palette.primary.main, mr: 1.5, fontSize: '2rem' }} />
        <Typography variant="h5" fontWeight="bold">
          Simulador de Plano Ideal
        </Typography>
        <Tooltip 
          title="Este simulador analisa suas necessidades e recomenda o melhor plano para você." 
          placement="top"
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 600 }}
        >
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" fontWeight="medium">
                Quantos leads você gerencia?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {leadCount} leads
              </Typography>
            </Box>
            <StyledSlider
              value={leadCount}
              onChange={(_, newValue) => setLeadCount(newValue as number)}
              min={0}
              max={500}
              valueLabelDisplay="auto"
              aria-labelledby="lead-count-slider"
            />
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" fontWeight="medium">
                Quantos produtos você cadastra?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {productCount} produtos
              </Typography>
            </Box>
            <StyledSlider
              value={productCount}
              onChange={(_, newValue) => setProductCount(newValue as number)}
              min={0}
              max={100}
              valueLabelDisplay="auto"
              aria-labelledby="product-count-slider"
            />
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" fontWeight="medium">
                Uso do WhatsApp IA Bot
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {whatsappUsage}% de uso
              </Typography>
            </Box>
            <StyledSlider
              value={whatsappUsage}
              onChange={(_, newValue) => setWhatsappUsage(newValue as number)}
              min={0}
              max={100}
              valueLabelDisplay="auto"
              aria-labelledby="whatsapp-usage-slider"
            />
          </Box>
          
          <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
            <InputLabel id="billing-cycle-label">Ciclo de Pagamento</InputLabel>
            <Select
              labelId="billing-cycle-label"
              value={billingCycle}
              onChange={handleBillingCycleChange}
              label="Ciclo de Pagamento"
            >
              <MenuItem value="monthly">Mensal</MenuItem>
              <MenuItem value="annual">Anual (20% de desconto)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Box sx={{ 
            p: 3, 
            background: recommendedPlan.gradient,
            color: 'white',
            borderRadius: '12px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <Box sx={{ 
              position: 'absolute', 
              top: '-20px', 
              right: '-20px', 
              width: '120px', 
              height: '120px', 
              borderRadius: '50%', 
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }} />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
                <Typography variant="h6" fontWeight="bold">
                  Plano Recomendado
                </Typography>
              </Box>
              
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {recommendedPlan.name}
              </Typography>
              
              <Box sx={{ mt: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  {billingCycle === 'monthly' ? 'Valor Mensal' : 'Valor Mensal (no plano anual)'}:
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  R$ {billingCycle === 'monthly' 
                    ? pricing.monthly.toFixed(2) 
                    : pricing.annual.toFixed(2)
                  }
                  <Typography component="span" variant="body2" sx={{ ml: 1, opacity: 0.8 }}>
                    {billingCycle === 'monthly' ? '/mês' : '/mês*'}
                  </Typography>
                </Typography>
                
                {billingCycle === 'annual' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Valor total anual:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      R$ {pricing.totalAnnual.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                      <Box component="span" sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.2)', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: '4px',
                        mr: 1,
                        fontWeight: 'bold'
                      }}>
                        Economia:
                      </Box>
                      R$ {pricing.savings.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ 
                  mt: 1,
                  bgcolor: 'white',
                  color: recommendedPlan.color,
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  },
                  py: 1.2,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Escolher Este Plano
              </Button>
              
              {billingCycle === 'annual' && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', opacity: 0.8 }}>
                  *Valor mensal com desconto de 20% no plano anual
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PlanSimulator;
